import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, withCsrfHeader } from "@/lib/queryClient";
import { PlatformLayout } from "./platform-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Bug, Zap, AlertTriangle, Clock, CheckCircle, XCircle, Filter, Eye, Trash2, Copy, RefreshCw, Sparkles, Loader2, Wand2, ClipboardCopy, ExternalLink } from "lucide-react";
import type { BetaFeedback, FixPromptStatus } from "@shared/schema";

type FixPromptResponse = {
  fixPrompt: string | null;
  fixPromptStatus: FixPromptStatus | null;
  fixPromptError: string | null;
  fixPromptGeneratedAt: string | null;
};

function FixPromptSection({ feedback }: { feedback: BetaFeedback }) {
  const { toast } = useToast();
  const [draft, setDraft] = useState("");
  const [edited, setEdited] = useState(false);

  const { data, isLoading } = useQuery<FixPromptResponse>({
    queryKey: ["/api/platform/feedback", feedback.id, "fix-prompt"],
    queryFn: async () => {
      const res = await fetch(`/api/platform/feedback/${feedback.id}/fix-prompt`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch fix prompt");
      return res.json();
    },
    // Poll while a generation is in flight so the prompt appears automatically.
    refetchInterval: (query) => (query.state.data?.fixPromptStatus === "pending" ? 3000 : false),
  });

  // Sync the editable draft from the server prompt unless the admin has started
  // editing (so we don't clobber their in-progress edits on a refetch).
  useEffect(() => {
    if (!edited && typeof data?.fixPrompt === "string") {
      setDraft(data.fixPrompt);
    }
  }, [data?.fixPrompt, edited]);

  const generate = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/platform/feedback/${feedback.id}/fix-prompt/generate`, {
        method: "POST",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: "{}",
      });
      if (!res.ok) throw new Error("Failed to start generation");
      return res.json();
    },
    onSuccess: () => {
      setEdited(false);
      queryClient.invalidateQueries({ queryKey: ["/api/platform/feedback", feedback.id, "fix-prompt"] });
      toast({ title: "Generating fix prompt…", description: "This usually takes a few seconds." });
    },
    onError: () => toast({ title: "Failed to start generation", variant: "destructive" }),
  });

  const status = data?.fixPromptStatus ?? null;
  const isEnhancement = feedback.feedbackType === "enhancement";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      toast({ title: "Copied", description: "Fix prompt copied to clipboard." });
    } catch {
      toast({ title: "Copy failed", description: "Could not access the clipboard.", variant: "destructive" });
    }
  };

  return (
    <div className="border-t pt-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          <Sparkles className="w-4 h-4 text-blue-500" />
          Proposed Fix / Replit Prompt
        </Label>
        <div className="flex items-center gap-2">
          {status === "pending" && (
            <Badge variant="outline" className="bg-blue-500/10 text-blue-700" data-testid="status-fix-prompt">
              <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Generating…
            </Badge>
          )}
          {status === "ready" && (
            <Badge variant="outline" className="bg-green-500/10 text-green-700" data-testid="status-fix-prompt">
              <CheckCircle className="w-3 h-3 mr-1" /> Ready
            </Badge>
          )}
          {status === "failed" && (
            <Badge variant="outline" className="bg-red-500/10 text-red-700" data-testid="status-fix-prompt">
              <XCircle className="w-3 h-3 mr-1" /> Failed
            </Badge>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : status === null ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            {isEnhancement
              ? "No fix prompt yet. Generate one to get a paste-ready Replit prompt for this enhancement."
              : "No fix prompt yet — generate one to get a paste-ready Replit prompt."}
          </p>
          <Button
            size="sm"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            data-testid="button-generate-fix-prompt"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Generate fix prompt
          </Button>
        </div>
      ) : status === "pending" ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4" data-testid="text-fix-prompt-pending">
          <Loader2 className="w-4 h-4 animate-spin" /> The agent is drafting a fix prompt…
        </div>
      ) : (
        <div className="space-y-3">
          {status === "failed" && (
            <div
              className="p-3 rounded-md bg-red-500/10 text-red-700 text-sm"
              data-testid="text-fix-prompt-error"
            >
              {data?.fixPromptError || "Generation failed."}
            </div>
          )}
          {(status === "ready" || (draft && draft.length > 0)) && (
            <Textarea
              className="font-mono text-xs min-h-[220px]"
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setEdited(true);
              }}
              data-testid="input-fix-prompt"
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              onClick={handleCopy}
              disabled={!draft}
              data-testid="button-copy-fix-prompt"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => generate.mutate()}
              disabled={generate.isPending}
              data-testid="button-regenerate-fix-prompt"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Regenerate
            </Button>
            {data?.fixPromptGeneratedAt && (
              <span className="text-xs text-muted-foreground" data-testid="text-fix-prompt-generated-at">
                Generated {new Date(data.fixPromptGeneratedAt).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const feedbackTypeConfig = {
  enhancement: { label: "Enhancement", icon: Zap, color: "bg-blue-500/10 text-blue-700 border-blue-200" },
  bug: { label: "Bug Report", icon: Bug, color: "bg-yellow-500/10 text-yellow-700 border-yellow-200" },
  critical: { label: "Critical Issue", icon: AlertTriangle, color: "bg-red-500/10 text-red-700 border-red-200" },
};

const statusConfig = {
  new: { label: "New", icon: Clock, color: "bg-gray-500/10 text-gray-700" },
  acknowledged: { label: "Acknowledged", icon: MessageSquare, color: "bg-blue-500/10 text-blue-700" },
  in_progress: { label: "In Progress", icon: Clock, color: "bg-yellow-500/10 text-yellow-700" },
  resolved: { label: "Resolved", icon: CheckCircle, color: "bg-green-500/10 text-green-700" },
  wont_fix: { label: "Won't Fix", icon: XCircle, color: "bg-gray-500/10 text-gray-500" },
};

const priorityConfig = {
  low: { label: "Low", color: "bg-gray-500/10 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-500/10 text-blue-700" },
  high: { label: "High", color: "bg-orange-500/10 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-500/10 text-red-700" },
};

export function FeedbackContent() {
  const { toast } = useToast();
  const [selectedFeedback, setSelectedFeedback] = useState<BetaFeedback | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [filters, setFilters] = useState({
    feedbackType: "all",
    status: "all",
    priority: "all",
  });

  const queryString = new URLSearchParams({
    feedbackType: filters.feedbackType,
    status: filters.status,
    priority: filters.priority,
  }).toString();

  const { data: feedback = [], isLoading } = useQuery<BetaFeedback[]>({
    queryKey: ["/api/platform/feedback", queryString],
    queryFn: async () => {
      const res = await fetch(`/api/platform/feedback?${queryString}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch feedback");
      return res.json();
    },
  });

  const updateFeedback = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const res = await fetch(`/api/platform/feedback/${id}`, {
        method: "PATCH",
        headers: await withCsrfHeader({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Failed to update feedback");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/feedback"] });
      toast({ title: "Feedback updated" });
    },
    onError: () => {
      toast({ title: "Failed to update feedback", variant: "destructive" });
    },
  });

  const deleteFeedback = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/platform/feedback/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: await withCsrfHeader(),
      });
      if (!res.ok) throw new Error("Failed to delete feedback");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/feedback"] });
      toast({ title: "Feedback deleted" });
      setDetailOpen(false);
      setSelectedFeedback(null);
    },
    onError: () => {
      toast({ title: "Failed to delete feedback", variant: "destructive" });
    },
  });

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const buildAgentContext = (item: BetaFeedback): string => {
    const typeLabel =
      item.feedbackType === "critical" ? "Critical Issue" :
      item.feedbackType === "bug" ? "Bug Report" : "Report";
    const urgencyLabel = item.urgency
      ? item.urgency.charAt(0).toUpperCase() + item.urgency.slice(1)
      : "—";
    const attachmentSummary = (item.attachments as any[] | null)?.length
      ? (item.attachments as any[]).map((a: any) => `${a.filename} (${Math.round(a.size / 1024)} KB)`).join(", ")
      : null;
    return [
      "=== CLARIK DEBUG CONTEXT ===",
      `Type:         ${typeLabel}`,
      `Urgency:      ${urgencyLabel}`,
      `Submitted:    ${new Date(item.createdAt).toISOString().replace("T", " ").slice(0, 19)} UTC`,
      `Feedback ID:  ${item.id}`,
      "",
      `WorkspaceID:  ${item.workspaceId}`,
      `User:         ${[item.userName, item.userEmail ? `<${item.userEmail}>` : ""].filter(Boolean).join(" ")}`,
      `UserID:       ${item.userId}`,
      item.pageUrl ? `Page:         ${item.pageUrl}` : null,
      "",
      `Title: ${item.title}`,
      "",
      "Description:",
      item.description,
      attachmentSummary ? `\nAttachments: ${attachmentSummary}` : null,
      "============================",
    ].filter((l) => l !== null).join("\n");
  };

  const handleCopyForAgent = async (item: BetaFeedback) => {
    try {
      await navigator.clipboard.writeText(buildAgentContext(item));
      toast({ title: "Copied for Agent", description: "Paste this into the Replit agent chat." });
    } catch {
      toast({ title: "Copy failed", description: "Could not access clipboard.", variant: "destructive" });
    }
  };

  const handleViewDetail = (item: BetaFeedback) => {
    setSelectedFeedback(item);
    setDetailOpen(true);
  };

  const handleUpdateAndClose = (id: string, updates: any) => {
    updateFeedback.mutate({ id, updates });
    if (selectedFeedback) {
      setSelectedFeedback({ ...selectedFeedback, ...updates });
    }
  };

  return (
    <>
      <div className="p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" data-testid="text-page-title">BetaTest Feedback</h1>
          <p className="text-muted-foreground">
            Manage feedback from workspace users
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <CardTitle className="text-sm font-medium">Filters</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="w-40">
                <Label className="text-xs mb-1 block">Type</Label>
                <Select
                  value={filters.feedbackType}
                  onValueChange={(value) => setFilters({ ...filters, feedbackType: value })}
                >
                  <SelectTrigger data-testid="select-filter-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="enhancement">Enhancement</SelectItem>
                    <SelectItem value="bug">Bug</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs mb-1 block">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => setFilters({ ...filters, status: value })}
                >
                  <SelectTrigger data-testid="select-filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="acknowledged">Acknowledged</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="wont_fix">Won't Fix</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40">
                <Label className="text-xs mb-1 block">Priority</Label>
                <Select
                  value={filters.priority}
                  onValueChange={(value) => setFilters({ ...filters, priority: value })}
                >
                  <SelectTrigger data-testid="select-filter-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Feedback ({feedback.length})</CardTitle>
            <CardDescription>
              User-submitted issues and enhancement requests
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
              </div>
            ) : feedback.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No feedback found with current filters</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedback.map((item) => {
                    const typeConfig = feedbackTypeConfig[item.feedbackType as keyof typeof feedbackTypeConfig] || feedbackTypeConfig.enhancement;
                    const statusCfg = statusConfig[item.status as keyof typeof statusConfig] || statusConfig.new;
                    const priorityCfg = item.priority ? priorityConfig[item.priority as keyof typeof priorityConfig] : null;
                    const TypeIcon = typeConfig.icon;

                    return (
                      <TableRow key={item.id} data-testid={`row-feedback-${item.id}`}>
                        <TableCell>
                          <Badge variant="outline" className={typeConfig.color}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium max-w-xs truncate">
                          {item.title}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {item.userName || item.userEmail}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={statusCfg.color}>
                            {statusCfg.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {priorityCfg ? (
                            <Badge variant="outline" className={priorityCfg.color}>
                              {priorityCfg.label}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(item.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleViewDetail(item)}
                            data-testid={`button-view-${item.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedFeedback && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {(() => {
                      const typeConfig = feedbackTypeConfig[selectedFeedback.feedbackType as keyof typeof feedbackTypeConfig] || feedbackTypeConfig.enhancement;
                      const TypeIcon = typeConfig.icon;
                      return (
                        <Badge variant="outline" className={typeConfig.color}>
                          <TypeIcon className="w-3 h-3 mr-1" />
                          {typeConfig.label}
                        </Badge>
                      );
                    })()}
                  </div>
                  <DialogTitle>{selectedFeedback.title}</DialogTitle>
                  <DialogDescription>
                    Submitted by {selectedFeedback.userName || selectedFeedback.userEmail} on {formatDate(selectedFeedback.createdAt)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div>
                    <Label className="text-sm font-medium">Description</Label>
                    <div className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                      {selectedFeedback.description}
                    </div>
                  </div>

                  {selectedFeedback.pageUrl && (
                    <div>
                      <Label className="text-sm font-medium">Page URL</Label>
                      <div className="mt-1 p-2 bg-muted rounded-md text-sm font-mono break-all">
                        {selectedFeedback.pageUrl}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Status</Label>
                      <Select
                        value={selectedFeedback.status}
                        onValueChange={(value) => handleUpdateAndClose(selectedFeedback.id, { status: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="select-detail-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="acknowledged">Acknowledged</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="resolved">Resolved</SelectItem>
                          <SelectItem value="wont_fix">Won't Fix</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Priority</Label>
                      <Select
                        value={selectedFeedback.priority || "none"}
                        onValueChange={(value) => handleUpdateAndClose(selectedFeedback.id, { priority: value === "none" ? null : value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="select-detail-priority">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Unset</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Admin Notes</Label>
                    <Textarea
                      className="mt-1"
                      placeholder="Add notes or response for the user..."
                      value={selectedFeedback.adminNotes || ""}
                      onChange={(e) => setSelectedFeedback({ ...selectedFeedback, adminNotes: e.target.value })}
                      rows={3}
                      data-testid="input-admin-notes"
                    />
                    <Button
                      size="sm"
                      className="mt-2"
                      onClick={() => updateFeedback.mutate({ id: selectedFeedback.id, updates: { adminNotes: selectedFeedback.adminNotes } })}
                      disabled={updateFeedback.isPending}
                      data-testid="button-save-notes"
                    >
                      Save Notes
                    </Button>
                  </div>

                  <FixPromptSection feedback={selectedFeedback} />
                </div>

                <DialogFooter className="gap-2 flex-wrap">
                  <Button
                    variant="destructive"
                    onClick={() => deleteFeedback.mutate(selectedFeedback.id)}
                    disabled={deleteFeedback.isPending}
                    data-testid="button-delete-feedback"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    onClick={() => handleCopyForAgent(selectedFeedback)}
                    data-testid="button-copy-for-agent"
                  >
                    <ClipboardCopy className="w-4 h-4 mr-2" />
                    Copy for Agent
                  </Button>
                  <Button
                    variant="outline"
                    asChild
                  >
                    <a
                      href={`/feedback-copy/${selectedFeedback.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-copy-page"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Copy Page
                    </a>
                  </Button>
                  <Button variant="outline" onClick={() => setDetailOpen(false)}>
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

export default function PlatformFeedback() {
  return <PlatformLayout><FeedbackContent /></PlatformLayout>;
}
